// Validate source before Mermaid's separate strict rendering mode. Keep this policy identical in
// the API and browser; normalization is deliberately explicit rather than hidden in validation.
const UNSAFE_CONTENT = /(?:%%\{|^\s*---|\b(?:click|callback)\s|\b(?:href|xlink:href)\s*=|<\s*\/?\s*[a-z!]|javascript\s*:|data\s*:|https?:\/\/|url\s*\(|@\s*\{)/im;
const STYLE_COMMAND = /(?:^|;)\s*(?:style|classDef|linkStyle|class)\s/im;
const STYLE_LINE = /^[ \t]*(?:style[ \t]+[\w.-]+|classDef[ \t]+[\w,.-]+|linkStyle[ \t]+(?:default|\d+(?:,\d+)*))[ \t]+[^;\n]+;?[ \t]*$/;
const CLASS_LINE = /^[ \t]*class[ \t]+[\w,.-]+[ \t]+[\w,.-]+;?[ \t]*$/;
/** Repair smart quotes only when they are unambiguous square-node label delimiters. */
function normalizeNodeLabelQuotes(source) {
    if (!/^flowchart\b/.test(source))
        return source;
    let brackets = 0;
    let quoted = false;
    let description = false;
    return source
        .split('\n')
        .map((line) => {
        if (description) {
            if (/^\s*}\s*$/.test(line))
                description = false;
            return line;
        }
        if (/^\s*accDescr\s*\{/.test(line)) {
            description = !line.includes('}');
            return line;
        }
        if (/^\s*(?:%%|accTitle\s*:|accDescr\s*:)/.test(line))
            return line;
        let result = '';
        let edgeLabel = false;
        for (let index = 0; index < line.length; index++) {
            const char = line[index] ?? '';
            if (char === '\\' && quoted) {
                result += char + (line[++index] ?? '');
                continue;
            }
            if (char === '"')
                quoted = !quoted;
            if (!quoted) {
                if (!brackets && char === '%' && line[index + 1] === '%')
                    return result + line.slice(index);
                if (!brackets && char === '|')
                    edgeLabel = !edgeLabel;
                if (char === '[' && !edgeLabel) {
                    const end = line.indexOf('”]', index + 2);
                    const label = line.slice(index + 2, end);
                    if (!brackets &&
                        line[index + 1] === '“' &&
                        end >= 0 &&
                        /[A-Za-z_][\w.-]*$/.test(line.slice(0, index)) &&
                        !/[“”"\\]/.test(label)) {
                        result += `["${label}"]`;
                        index = end + 1;
                        continue;
                    }
                    brackets++;
                }
                else if (char === ']' && !edgeLabel)
                    brackets = Math.max(0, brackets - 1);
            }
            result += char;
        }
        return result;
    })
        .join('\n');
}
/**
 * Mermaid has no backslash escapes: a generated \" inside a quoted label is a parse error and a
 * literal \n is noise, both seen in live diagrams that failed on their first attempt. Only the
 * inside of quoted labels changes; comments and accessibility lines stay as written.
 */
function repairLabelEscapes(source) {
    if (!/^flowchart\b/.test(source))
        return source;
    return source
        .split('\n')
        .map((line) => /^\s*(?:%%|accTitle\s*:|accDescr)/.test(line)
        ? line
        : line.replace(/"((?:[^"\\]|\\.)*)"/g, (match, label) => label.includes('\\')
            ? `"${label.replace(/\\"/g, '#quot;').replace(/\\{1,2}n/g, ' ')}"`
            : match))
        .join('\n');
}
/**
 * Normalize generated Mermaid without changing semantic nodes or edges. Accept the graph alias
 * and one complete outer Mermaid fence; remove only standalone presentation commands so the
 * product's theme owns the colors. Repair unambiguous paired smart quotes delimiting square
 * flowchart node labels, preserving quoted prose and ambiguous labels. Unsafe content is retained
 * for isSafeMioDiagram to reject,
 * including payloads hidden inside an otherwise removable style declaration.
 */
export function normalizeMioDiagram(source) {
    let normalized = source.replace(/\r\n?/g, '\n').trim();
    const fenced = /^(`{3,}|~{3,})[ \t]*(?:mermaid)?[ \t]*\n([\s\S]*?)\n\1[ \t]*$/i.exec(normalized);
    if (fenced)
        normalized = (fenced[2] ?? '').trim();
    normalized = normalized.replace(/^graph(?=\s+(?:TD|TB|LR|RL|BT)\b)/, 'flowchart');
    if (UNSAFE_CONTENT.test(normalized))
        return normalized;
    return normalizeNodeLabelQuotes(repairLabelEscapes(normalized
        .split('\n')
        .filter((line) => {
        if (CLASS_LINE.test(line))
            return false;
        // A line containing another statement or a node/edge is not a standalone CSS declaration.
        return !STYLE_LINE.test(line) || /--|==|~~|->|<-|[{}[\]]/.test(line);
    })
        .join('\n')
        .trim()));
}
/** Validate the exact string that will be stored/rendered; this function never rewrites input. */
export function isSafeMioDiagram(source) {
    return (/^\s*(?:flowchart\s+(?:TD|TB|LR|RL|BT)|sequenceDiagram)\b/.test(source) &&
        !UNSAFE_CONTENT.test(source) &&
        !STYLE_COMMAND.test(source));
}
